/**
 * Date utility functions for handling date-only strings without timezone conversions
 *
 * These functions treat date strings like "2025-12-25" as local dates, avoiding
 * JavaScript's automatic UTC conversion which can cause dates to shift by a day.
 */

/**
 * Parse a date string like "2025-12-25" as a local date (NOT UTC)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed in JavaScript
};

/**
 * Format a Date object as "YYYY-MM-DD" string using LOCAL timezone
 * @param {Date} date - Date object to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatLocalDateString = (date) => {
  if (!date || !(date instanceof Date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date string for display using local timezone
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatLocalDateForDisplay = (dateStr, options = {}) => {
  const date = parseLocalDate(dateStr);
  if (!date) return '';

  const defaultOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  };

  return date.toLocaleDateString('en-GB', { ...defaultOptions, ...options });
};

/**
 * Get the year from a date string without timezone conversion
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {number} Year
 */
export const getLocalYear = (dateStr) => {
  if (!dateStr) return null;
  const date = parseLocalDate(dateStr);
  return date ? date.getFullYear() : null;
};

/**
 * Check if a date string represents a date in the past (local timezone)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is in the past
 */
export const isLocalDatePast = (dateStr) => {
  const date = parseLocalDate(dateStr);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date < today;
};

/**
 * Check if a date string represents a date in the future (local timezone)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is in the future or today
 */
export const isLocalDateUpcoming = (dateStr) => {
  const date = parseLocalDate(dateStr);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date >= today;
};
